import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useT } from "../i18n";

const COVER = "https://www.wingchun.gr/wp-content/uploads/2026/07/%CE%92%CE%B9%CE%B2%CE%BB%CE%AF%CE%BF-Wing-Chun-%CE%97-%CE%A4%CE%AD%CF%87%CE%BD%CE%B7-%CF%84%CE%B7%CF%82-%CE%91%CF%85%CF%84%CE%BF%CE%AC%CE%BC%CF%85%CE%BD%CE%B1%CF%82.png";
const SPREAD1 = "https://www.wingchun.gr/wp-content/uploads/2026/07/%CE%94%CE%B5%CE%AF%CE%B3%CE%BC%CE%B1-%CF%83%CE%B5%CE%BB%CE%AF%CE%B4%CE%B1%CF%82-%CE%B1%CF%80%CF%8C-%CF%84%CE%BF-%CE%B2%CE%B9%CE%B2%CE%BB%CE%AF%CE%BF-Wing-Chun-%CE%97-%CE%A4%CE%AD%CF%87%CE%BD%CE%B7-%CF%84%CE%B7%CF%82-%CE%91%CF%85%CF%84%CE%BF%CE%AC%CE%BC%CF%85%CE%BD%CE%B1%CF%82.png";
const SPREAD2 = "https://www.wingchun.gr/wp-content/uploads/2026/07/%CE%95%CF%83%CF%89%CF%84%CE%B5%CF%81%CE%B9%CE%BA%CE%AE-%CF%83%CE%B5%CE%BB%CE%AF%CE%B4%CE%B1-%CE%B2%CE%B9%CE%B2%CE%BB%CE%AF%CE%BF%CF%85-Wing-Chun-%CE%BC%CE%B5-%CF%84%CE%B5%CF%87%CE%BD%CE%B9%CE%BA%CE%AD%CF%82-%CE%B1%CF%85%CF%84%CE%BF%CE%AC%CE%BC%CF%85%CE%BD%CE%B1%CF%82.jpg";
const SPREAD3 = "https://www.wingchun.gr/wp-content/uploads/2026/07/wing_chun_page_212.jpg";
const SPREAD4 = "https://www.wingchun.gr/wp-content/uploads/2026/07/wing_chun_page_213.jpg";

const Book = () => {
  const { lang } = useLanguage();
  const t = useT(lang);

  const details = lang === "el"
    ? [
        ["Συγγραφέας", "Sifu Νίκος Αγαθόπουλος"],
        ["Σελίδες", "242"],
        ["Μέγεθος", "A4"],
        ["Έκδοση", "Έγχρωμη"],
        ["Τιμή", "50€"],
        ["Διαθεσιμότητα", "Κατόπιν επικοινωνίας"],
      ]
    : [
        ["Author", "Sifu Nikos Agathopoulos"],
        ["Pages", "242"],
        ["Format", "A4"],
        ["Edition", "Full color"],
        ["Price", "€50"],
        ["Availability", "By request"],
      ];

  const includes = lang === "el"
    ? [
        "Οι βασικές αρχές: κεντρική γραμμή, οικονομία της κίνησης, στάση σώματος",
        "Αναλυτικές τεχνικές αυτοάμυνας με φωτογραφικό υλικό",
        "QR Codes που συνδέουν τη θεωρία με βίντεο εφαρμογής",
        "Φιλοσοφία και ιστορικό υπόβαθρο του παραδοσιακού Wing Chun",
        "Οδηγός για αρχάριους και προχωρημένους ασκούμενους",
      ]
    : [
        "The core principles: centerline, economy of motion, body posture",
        "Detailed self-defense techniques with photographic material",
        "QR Codes linking theory to video demonstrations",
        "Philosophy and historical background of traditional Wing Chun",
        "A guide for beginners and advanced practitioners",
    ];

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="absolute inset-0 opacity-30">
          <img src="https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=1920&q=80" alt="" className="w-full h-full object-cover img-moody" />
          <div className="absolute inset-0 bg-[#0A0A0A]/85" />
        </div>
        <div className="absolute right-[-10%] top-[-6%] cn-bg">書</div>

        <div className="container-wc relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="flex items-center gap-3 mb-4">
              <span className="divider-gold" />
              <span className="eyebrow">{lang === "el" ? "ΒΙΒΛΙΟ" : "THE BOOK"}</span>
            </div>
            <h1 className="h-display text-5xl md:text-7xl mb-6">
              Wing Chun
              <span className="block text-gold font-serif italic text-3xl md:text-4xl mt-3">
                {lang === "el" ? "— Η Τέχνη της Αυτοάμυνας" : "— The Art of Self-Defense"}
              </span>
            </h1>
            <p className="text-dim text-lg leading-relaxed max-w-2xl mb-8">
              {lang === "el"
                ? "Ένας ολοκληρωμένος οδηγός για τις βασικές αρχές, τη φιλοσοφία και την πρακτική εφαρμογή του Παραδοσιακού Wing Chun, από τον Sifu Νίκο Αγαθόπουλο."
                : "A complete guide to the fundamental principles, philosophy and practical application of traditional Wing Chun, by Sifu Nikos Agathopoulos."}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link to="/contact" data-testid="book-order-cta" className="btn-primary">
                {lang === "el" ? "Παραγγελία" : "Order now"} <ArrowRight size={16} />
              </Link>
              <span className="text-gold font-serif text-3xl">50€</span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-primary/20 blur-3xl" />
              <img
                src={COVER}
                alt="Wing Chun - Η Τέχνη της Αυτοάμυνας"
                className="relative w-full h-auto shadow-2xl shadow-black/60"
                style={{ transform: "perspective(1000px) rotateY(-8deg)" }}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=900&q=80";
                  e.currentTarget.style.transform = "perspective(1000px) rotateY(-8deg)";
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* DETAILS + INCLUDES */}
      <section className="py-20 md:py-24">
        <div className="container-wc grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="card">
              <span className="font-cn text-gold text-3xl block mb-4">詳</span>
              <h3 className="font-serif text-2xl mb-6">{lang === "el" ? "Πληροφορίες" : "Details"}</h3>
              <dl className="divide-y divide-goldSoft">
                {details.map(([k, v]) => (
                  <div key={k} className="flex justify-between py-3">
                    <dt className="text-dim text-sm uppercase tracking-widest">{k}</dt>
                    <dd className="text-[#F5F5F0] font-serif">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <span className="divider-gold" />
              <span className="eyebrow">{lang === "el" ? "Τι περιλαμβάνει" : "What's inside"}</span>
            </div>
            <h2 className="h-display text-4xl md:text-5xl mb-10">
              {lang === "el" ? "Θεωρία που γίνεται πράξη." : "Theory that becomes practice."}
            </h2>
            <ul className="space-y-4">
              {includes.map((item) => (
                <li key={item} className="flex gap-4 items-start">
                  <Check size={20} className="text-gold flex-shrink-0 mt-1" />
                  <span className="text-[#E8E8E0] leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-dim text-sm mt-8 italic leading-relaxed">
              {lang === "el"
                ? "«Δεν είναι απλώς ένα τεχνικό εγχειρίδιο. Είναι μια προσωπική κατάθεση γνώσης, εμπειρίας και σεβασμού προς την τέχνη του Wing Chun.»"
                : "\"Not just a technical manual. It is a personal offering of knowledge, experience and respect for the art of Wing Chun.\""}
            </p>
          </div>
        </div>
      </section>

      {/* SPREADS GALLERY */}
      <section className="py-20 bg-surface">
        <div className="container-wc">
          <div className="flex items-center gap-3 mb-4">
            <span className="divider-gold" />
            <span className="eyebrow">{lang === "el" ? "Δείγμα σελίδων" : "Page preview"}</span>
          </div>
          <h2 className="h-display text-4xl md:text-5xl mb-12">
            {lang === "el" ? "Μια ματιά στο εσωτερικό" : "A look inside"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[SPREAD1, SPREAD2, SPREAD3, SPREAD4].map((src, idx) => (
              <div key={src} className="overflow-hidden border border-goldSoft group bg-black/40 aspect-[4/3]">
                <img
                  src={src}
                  alt=""
                  className="w-full h-full object-cover img-moody group-hover:scale-[1.03]"
                  style={{ transition: "transform 700ms ease" }}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    const fallbacks = [
                      "https://images.unsplash.com/photo-1603143600562-e765fc279ae8?w=900",
                      "https://images.pexels.com/photos/6253176/pexels-photo-6253176.jpeg?w=900",
                      "https://images.unsplash.com/photo-1555597673-b21d5c935865?w=900",
                      "https://images.unsplash.com/photo-1544717305-2782549b5136?w=900",
                    ];
                    e.currentTarget.src = fallbacks[idx];
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 text-center container-wc">
        <span className="font-cn text-gold text-5xl block mb-6">書</span>
        <h2 className="h-display text-3xl md:text-5xl mb-4">
          {lang === "el" ? "Έτοιμοι να ξεκινήσετε το ταξίδι σας;" : "Ready to begin your journey?"}
        </h2>
        <p className="text-dim mb-8 max-w-xl mx-auto">
          {lang === "el"
            ? "Για παραγγελία του βιβλίου ή περισσότερες πληροφορίες, επικοινωνήστε μαζί μας."
            : "To order the book or for more information, get in touch with us."}
        </p>
        <Link to="/contact" data-testid="book-cta-bottom" className="btn-primary">
          {lang === "el" ? "Παραγγελία / Επικοινωνία" : "Order / Contact"} <ArrowRight size={16} />
        </Link>
      </section>
    </div>
  );
};

export default Book;
